// Stage 1 — Patient Self-Report questionnaire (P0–P25), sourced from QP_KLevel_Questions.
// This module only collects answers; K-level is calculated by the backend.

export interface Option {
  value: string;
  label: string;
}

export type Stage1InputType =
  | "single-select"
  | "single-select-with-text"
  | "single-select-with-conditional-text"
  | "multi-select"
  | "yes-no"
  | "yes-no-with-slider"
  | "slider"
  | "date";

export type Stage1Block =
  | "A. About your amputation"
  | "B. Your life before"
  | "C. How you move today"
  | "D. How you're feeling"
  | "E. Your goals"
  | "F. Self-Assessment";

export type ValidatedBy =
  "None" | "Physician" | "Prosthetist" | "Physician + Prosthetist";

export interface Stage1Question {
  id: string;
  block: Stage1Block;
  question: string;
  inputType: Stage1InputType;
  options?: Option[];
  kLevelSignal: boolean;
  lcdGate: boolean;
  validatedBy: ValidatedBy;
  priority: "Required" | "Useful";
  notApplicableIfBilateral?: boolean;
  onlyIfBilateral?: boolean;
  // "if bilateral + w/o prosthesis yet" = skip, per QP_KLevel_Questions: current-mobility
  // self-report items require an actual prosthesis to answer meaningfully when bilateral.
  notApplicableIfBilateralWithoutProsthesis?: boolean;
  // Shown only if patient has prosthetics at all (P0 answer is not "no-prosthetics-yet"),
  // regardless of bilateral status. Used by the new P0.1/P0.2 "same-or-similar" questions.
  notApplicableIfNoProsthesis?: boolean;
  // For "single-select-with-conditional-text": which option value reveals the follow-up
  // text box. Defaults to "yes" (P19's shape) when omitted.
  conditionalTextTrigger?: string;
  bilateralPairLabel?: "Left leg" | "Right leg";
  notes?: string;
}

const yesNo: Option[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

export const STAGE1_QUESTIONS: Stage1Question[] = [
  {
    id: "P0",
    block: "A. About your amputation",
    question: "Before we continue – do you currently wear prosthetic legs?",
    inputType: "single-select",
    options: [
      {
        value: "wears-regularly",
        label: "I have prosthetics and wear them regularly",
      },
      {
        value: "has-not-wearing",
        label: "I have prosthetics but don't wear them yet / rarely wear them",
      },
      { value: "no-prosthetics-yet", label: "I don't have prosthetics yet" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notes:
      "Gates S4–S20b for bilateral patients; corresponds to the prosthetics replacement flow.",
  },
  {
    id: "P0.1",
    block: "A. About your amputation",
    question: "Why are you looking for a new or different prosthetic?",
    inputType: "single-select-with-conditional-text",
    options: [
      { value: "doesnt-fit", label: "It doesn't fit correctly anymore" },
      { value: "old-worn-out", label: "It's getting old / worn out" },
      { value: "pain-or-blisters", label: "I'm having pain or blisters" },
      {
        value: "upgrade-technology",
        label: "I want an upgrade / better technology",
      },
      { value: "other", label: "Other – please describe" },
    ],
    kLevelSignal: false,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Useful",
    notApplicableIfNoProsthesis: true,
    conditionalTextTrigger: "other",
    notes:
      "NEW — agreed call 8-Jul-2026. Shown only if 'I have prosthetics' at P0; feeds Same-or-Similar check.",
  },
  {
    id: "P0.2",
    block: "A. About your amputation",
    question:
      "When did you receive your current prosthetic(s)? (approximate is fine)",
    inputType: "date",
    kLevelSignal: false,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Useful",
    notApplicableIfNoProsthesis: true,
    bilateralPairLabel: "Left leg",
    notes:
      "NEW — agreed call 8-Jul-2026. Repeated per leg if bilateral; shown only if 'I have prosthetics' at P0; feeds Same-or-Similar check.",
  },
  {
    id: "P0.2-right",
    block: "A. About your amputation",
    question:
      "When did you receive your current prosthetic(s)? (approximate is fine)",
    inputType: "date",
    kLevelSignal: false,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Useful",
    onlyIfBilateral: true,
    notApplicableIfNoProsthesis: true,
    bilateralPairLabel: "Right leg",
    notes: "Repeat of P0.2 for the right leg.",
  },
  {
    id: "P2",
    block: "A. About your amputation",
    question: "Is your amputation on one side, or both sides?",
    inputType: "single-select",
    options: [
      { value: "left", label: "One leg — left" },
      { value: "right", label: "One leg — right" },
      { value: "bilateral", label: "Both legs" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notes:
      "Bilateral → higher component complexity; asked before P1/P3/P5/P6 so those can be repeated per leg.",
  },
  {
    id: "P1",
    block: "A. About your amputation",
    question: "Which best describes your amputation?",
    inputType: "single-select",
    options: [
      { value: "below-knee", label: "Below the knee" },
      { value: "above-knee", label: "Above the knee" },
      { value: "partial-foot", label: "Part of the foot" },
      { value: "hip", label: "At the hip" },
      { value: "other-not-sure", label: "Other / not sure" },
    ],
    kLevelSignal: true,
    lcdGate: true,
    validatedBy: "Prosthetist",
    priority: "Required",
    bilateralPairLabel: "Left leg",
    notes:
      "Determines eligible component range and K-level ceiling; repeated per leg if bilateral and dates differ.",
  },
  {
    id: "P1-right",
    block: "A. About your amputation",
    question: "Which best describes your amputation?",
    inputType: "single-select",
    options: [
      { value: "below-knee", label: "Below the knee" },
      { value: "above-knee", label: "Above the knee" },
      { value: "partial-foot", label: "Part of the foot" },
      { value: "hip", label: "At the hip" },
      { value: "other-not-sure", label: "Other / not sure" },
    ],
    kLevelSignal: true,
    lcdGate: true,
    validatedBy: "Prosthetist",
    priority: "Required",
    onlyIfBilateral: true,
    bilateralPairLabel: "Right leg",
    notes:
      "Repeat of P1 for the right leg — bilateral amputees may have differing levels per side.",
  },
  {
    id: "P3",
    block: "A. About your amputation",
    question: "When did your amputation happen?",
    inputType: "date",
    kLevelSignal: false,
    lcdGate: false,
    validatedBy: "None",
    priority: "Useful",
    bilateralPairLabel: "Left leg",
    notes:
      "Time since amputation affects rehab potential; repeated per leg if bilateral and dates differ.",
  },
  {
    id: "P3-right",
    block: "A. About your amputation",
    question: "When did your amputation happen?",
    inputType: "date",
    kLevelSignal: false,
    lcdGate: false,
    validatedBy: "None",
    priority: "Useful",
    onlyIfBilateral: true,
    bilateralPairLabel: "Right leg",
    notes:
      "Repeat of P3 for the right leg — bilateral amputees may have differing dates per side.",
  },
  {
    id: "P4",
    block: "A. About your amputation",
    question: "What led to your amputation?",
    inputType: "single-select",
    options: [
      { value: "injury-accident", label: "An injury or accident" },
      { value: "diabetes", label: "Diabetes" },
      { value: "circulation", label: "Circulation/blood vessel problems" },
      { value: "cancer", label: "Cancer" },
      { value: "congenital", label: "Born this way" },
      { value: "other", label: "Other" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Physician",
    priority: "Required",
    notes:
      "Aetiology informs comorbidity risk; physician confirms/codes formally as ICD-10 at D2.",
  },
  {
    id: "P5",
    block: "A. About your amputation",
    question: "Do you feel your leg has fully healed from the surgery?",
    inputType: "single-select",
    options: [
      { value: "fully-healed", label: "Yes, fully healed" },
      { value: "still-healing", label: "No, still healing" },
      { value: "not-sure", label: "Not sure" },
    ],
    kLevelSignal: false,
    lcdGate: true,
    validatedBy: "Physician",
    priority: "Required",
    bilateralPairLabel: "Left leg",
    notes:
      "Flags prosthetic fitting readiness for LCD gate; repeated per leg if bilateral.",
  },
  {
    id: "P5-right",
    block: "A. About your amputation",
    question: "Do you feel your leg has fully healed from the surgery?",
    inputType: "single-select",
    options: [
      { value: "fully-healed", label: "Yes, fully healed" },
      { value: "still-healing", label: "No, still healing" },
      { value: "not-sure", label: "Not sure" },
    ],
    kLevelSignal: false,
    lcdGate: true,
    validatedBy: "Physician",
    priority: "Required",
    onlyIfBilateral: true,
    bilateralPairLabel: "Right leg",
    notes: "Repeat of P5 for the right leg.",
  },
  {
    id: "P6",
    block: "A. About your amputation",
    question:
      "Right now, how much pain do you feel in your residual limb? (0 = none, 10 = worst pain imaginable)",
    inputType: "slider",
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "None",
    priority: "Required",
    bilateralPairLabel: "Left leg",
    notes:
      "High pain may affect assessment reliability; distinct from phantom limb pain (P7); repeated per leg if bilateral.",
  },
  {
    id: "P6-right",
    block: "A. About your amputation",
    question:
      "Right now, how much pain do you feel in your residual limb? (0 = none, 10 = worst pain imaginable)",
    inputType: "slider",
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "None",
    priority: "Required",
    onlyIfBilateral: true,
    bilateralPairLabel: "Right leg",
    notes: "Repeat of P6 for the right leg.",
  },
  {
    id: "P7",
    block: "A. About your amputation",
    question:
      "Some people feel pain that seems to come from the part of the limb that's no longer there. Do you experience this?",
    inputType: "yes-no-with-slider",
    options: yesNo,
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Physician",
    priority: "Required",
    notes:
      "Phantom limb pain is independently associated with post-fitting non-use. Distinct from residual limb pain (P6). Flags for pain management plan before fitting.",
  },
  {
    id: "P8",
    block: "A. About your amputation",
    question: "How is your other leg or foot doing?",
    inputType: "single-select",
    options: [
      { value: "no-problems", label: "No problems" },
      {
        value: "mild-pain-stiffness",
        label: "Some pain or stiffness, but I walk normally",
      },
      { value: "affects-gait", label: "It affects how I walk" },
      { value: "cannot-bear-weight", label: "I can't put weight on it" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Physician",
    priority: "Required",
    notApplicableIfBilateral: true,
    notes:
      "Contralateral limb condition affects balance, compensatory gait patterns, and K-level ceiling. Physician verifies at D4; not applicable to bilateral patients.",
  },
  {
    id: "P9",
    block: "B. Your life before",
    question:
      "Before your amputation, could you walk around on your own without help?",
    inputType: "single-select",
    options: [
      { value: "fully-independent", label: "Yes, completely on my own" },
      {
        value: "needed-help",
        label: "I needed some help (cane, walker, person)",
      },
      { value: "wheelchair", label: "I used a wheelchair" },
      {
        value: "already-limited",
        label: "I was already limited in how I moved",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Physician + Prosthetist",
    priority: "Required",
    notes: "Pre-amputation baseline informs rehabilitation potential.",
  },
  {
    id: "P9.1",
    block: "B. Your life before",
    question:
      "Before your amputation, how long had you been unable to walk or only able to walk very little?",
    inputType: "single-select",
    options: [
      {
        value: "normal-until-amputation",
        label: "I was walking normally right up until the amputation",
      },
      {
        value: "few-weeks-months",
        label: "A few weeks or months of difficulty",
      },
      {
        value: "help-6mo-1yr",
        label: "Mostly with help for 6 months to 1 year",
      },
      {
        value: "help-over-1yr",
        label: "Mostly with help for more than 1 year",
      },
      {
        value: "help-several-years",
        label: "Already with help for several years",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Physician + Prosthetist",
    priority: "Required",
    notes:
      "NEW — agreed call 8-Jul-2026. May cap at K1/K2 regardless of AMPPro score.",
  },
  {
    id: "P10",
    block: "B. Your life before",
    question: "Before your amputation, how active was your day-to-day life?",
    inputType: "single-select",
    options: [
      { value: "mostly-sitting", label: "Mostly sitting/resting" },
      { value: "light-walking", label: "Light walking around" },
      {
        value: "regular-community-walking",
        label: "Walking regularly in my community",
      },
      {
        value: "sports-demanding-work",
        label: "Sports or physically demanding work",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Physician + Prosthetist",
    priority: "Required",
    notes: "Supports K3/K4 potential assessment.",
  },
  {
    id: "P11",
    block: "B. Your life before",
    question:
      "Before your amputation, were you working? What kind of activity did it involve?",
    inputType: "single-select-with-text",
    options: [
      { value: "desk-job", label: "Mostly sitting (desk job)" },
      { value: "light-physical", label: "Light physical work" },
      { value: "heavy-manual", label: "Heavy physical/manual work" },
      { value: "not-working", label: "Not working" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Physician + Prosthetist",
    priority: "Required",
    notes: "Heavy manual / physical → K4 potential flag.",
  },
  {
    id: "P12",
    block: "B. Your life before",
    question: "Before your amputation, did you use any of these?",
    inputType: "multi-select",
    options: [
      { value: "none", label: "None" },
      { value: "cane", label: "Cane" },
      { value: "walker", label: "Walker" },
      { value: "crutches", label: "Crutches" },
      { value: "wheelchair", label: "Wheelchair" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Physician + Prosthetist",
    priority: "Required",
    notes: "Prior device use sets baseline mobility expectation.",
  },
  {
    id: "P13",
    block: "C. How you move today",
    question: "Right now, how do you get around inside your home?",
    inputType: "single-select",
    options: [
      {
        value: "cannot-transfer",
        label: "I can't safely move/transfer, even with help",
      },
      {
        value: "short-distances-with-support",
        label:
          "I can walk short distances with a walker, cane, or wall for support",
      },
      {
        value: "moves-easily",
        label: "I move around easily and can manage small steps",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes:
      "Maps to AMPPro Phase 1 Q1 – indoor mobility. Skipped if bilateral + no prosthesis yet — prosthetist assesses in person once fitted.",
  },
  {
    id: "P14",
    block: "C. How you move today",
    question: "How do you get around outside your home?",
    inputType: "single-select",
    options: [
      { value: "does-not-go-outside", label: "I don't go outside at all" },
      {
        value: "car-mailbox-flat-ground",
        label: "Only to a car or mailbox, on flat smooth ground",
      },
      {
        value: "store-parking-avoids-terrain",
        label: "Around a store or parking lot, but I avoid grass/gravel/curbs",
      },
      {
        value: "confident-on-uneven-terrain",
        label: "Confidently over grass, gravel, curbs, and ramps",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes:
      "Community vs. household ambulator discriminator. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "P15",
    block: "C. How you move today",
    question:
      "Can you handle stairs, curbs, ramps, or uneven ground (like grass or gravel)?",
    inputType: "single-select",
    options: [
      { value: "avoids-completely", label: "No, I avoid these completely" },
      {
        value: "manages-with-handrail",
        label: "I can manage a curb or a few steps if there's a handrail",
      },
      {
        value: "no-trouble",
        label: "Yes, I can do stairs and uneven ground without much trouble",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes:
      "K2 vs. K3 self-report signal. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "P16",
    block: "C. How you move today",
    question:
      "Can you safely speed up, slow down, or change direction quickly if you need to – like crossing a street before the light changes?",
    inputType: "single-select",
    options: [
      {
        value: "single-pace-only",
        label: "No, I only walk at one steady pace",
      },
      {
        value: "can-change-speed-direction",
        label: "Yes, I can change speed or direction safely",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes:
      "Variable cadence = K3 minimum. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "P17",
    block: "C. How you move today",
    question:
      "Can you stand still for about 10 seconds without holding onto anything?",
    inputType: "yes-no",
    options: yesNo,
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes:
      "Basic standing balance screen. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "P18",
    block: "C. How you move today",
    question: "How long can you keep moving before you need to stop and rest?",
    inputType: "single-select",
    options: [
      { value: "under-1-min", label: "Less than 1 minute" },
      { value: "1-to-5-min", label: "1 to 5 minutes" },
      { value: "over-5-min", label: "More than 5 minutes" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Endurance indicator. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "P19",
    block: "C. How you move today",
    question:
      "Does your day-to-day life, job, or hobbies involve things like running, heavy lifting, or rough terrain?",
    inputType: "single-select-with-conditional-text",
    options: [
      { value: "no", label: "No" },
      { value: "yes", label: "Yes" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notes: "Yes + AMPPro ≥ 37 → K4 override flag for clinician.",
  },
  {
    id: "P20",
    block: "D. How you're feeling",
    question:
      "Over the past 2 weeks, how often have you had little interest or pleasure in doing things?",
    inputType: "single-select",
    options: [
      { value: "0", label: "Not at all" },
      { value: "1", label: "Several days" },
      { value: "2", label: "More than half the days" },
      { value: "3", label: "Nearly every day" },
    ],
    kLevelSignal: false,
    lcdGate: false,
    validatedBy: "Physician",
    priority: "Useful",
    notes:
      "PHQ-2 depression screen. Combined PHQ-2 score (P20+P21) ≥ 3 = positive screen → flag for clinical follow-up. Not a K-level input.",
  },
  {
    id: "P21",
    block: "D. How you're feeling",
    question:
      "Over the past 2 weeks, how often have you felt down, depressed, or hopeless?",
    inputType: "single-select",
    options: [
      { value: "0", label: "Not at all" },
      { value: "1", label: "Several days" },
      { value: "2", label: "More than half the days" },
      { value: "3", label: "Nearly every day" },
    ],
    kLevelSignal: false,
    lcdGate: false,
    validatedBy: "Physician",
    priority: "Useful",
    notes: "PHQ-2 Q2. Combined score shown to clinician only, not patient.",
  },
  {
    id: "P22",
    block: "D. How you're feeling",
    question:
      "Over the past 2 weeks, how often have you felt nervous, anxious, or on edge?",
    inputType: "single-select",
    options: [
      { value: "0", label: "Not at all" },
      { value: "1", label: "Several days" },
      { value: "2", label: "More than half the days" },
      { value: "3", label: "Nearly every day" },
    ],
    kLevelSignal: false,
    lcdGate: false,
    validatedBy: "Physician",
    priority: "Useful",
    notes:
      "GAD-2 anxiety screen. Combined GAD-2 score (P22+P23) ≥ 3 = positive screen.",
  },
  {
    id: "P23",
    block: "D. How you're feeling",
    question:
      "Over the past 2 weeks, how often have you been unable to stop or control worrying?",
    inputType: "single-select",
    options: [
      { value: "0", label: "Not at all" },
      { value: "1", label: "Several days" },
      { value: "2", label: "More than half the days" },
      { value: "3", label: "Nearly every day" },
    ],
    kLevelSignal: false,
    lcdGate: false,
    validatedBy: "Physician",
    priority: "Useful",
    notes:
      "GAD-2 Q2. PHQ-2/GAD-2 are screening tools, not diagnostic instruments. Positive screen does not block K-level assessment — it informs care planning.",
  },
  {
    id: "P24",
    block: "E. Your goals",
    question: "What's most important to you about getting a prosthetic?",
    inputType: "single-select",
    options: [
      { value: "safe-at-home", label: "Getting around safely at home" },
      {
        value: "neighbourhood-community",
        label: "Walking around my neighbourhood/community",
      },
      { value: "work-or-school", label: "Going back to work or school" },
      {
        value: "sports-active-lifestyle",
        label: "Returning to sports or an active lifestyle",
      },
    ],
    kLevelSignal: true,
    lcdGate: true,
    validatedBy: "Physician + Prosthetist",
    priority: "Required",
    notes: "CMS LCD gate: patient must be motivated to ambulate.",
  },
  {
    id: "P25",
    block: "E. Your goals",
    question:
      "Are you willing and able to take part in a rehab/training programme to learn to use your prosthetic?",
    inputType: "single-select",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
      { value: "not-sure", label: "Not sure" },
    ],
    kLevelSignal: false,
    lcdGate: true,
    validatedBy: "Physician",
    priority: "Required",
    notes: "LCD requires reasonable expectation of achieving functional state.",
  },
  {
    id: "P26",
    block: "E. Your goals",
    question:
      "Do you plan to return to the same type of work or activity after getting your prosthetic?",
    inputType: "single-select",
    options: [
      {
        value: "same-activity",
        label: "Yes, I plan to return to the same work or activity",
      },
      {
        value: "less-demanding",
        label: "No, I plan to do something less physically demanding",
      },
      {
        value: "more-demanding",
        label: "No, I plan to do something more physically demanding",
      },
      { value: "not-sure", label: "I'm not sure yet" },
    ],
    kLevelSignal: true,
    lcdGate: true,
    validatedBy: "Physician + Prosthetist",
    priority: "Required",
    notes:
      "NEW — agreed call 8-Jul-2026. Informs K3/K4 potential and durability requirements.",
  },
  {
    id: "S1",
    block: "F. Self-Assessment",
    question:
      "Sit on a firm chair without anything behind your back, with both feet flat on the floor. Can you stay sitting like this comfortably for about a minute, without needing to hold onto anything?",
    inputType: "single-select",
    options: [
      { value: "comfortable", label: "Yes, I can sit like this comfortably" },
      {
        value: "needs-support",
        label: "No, I need something to lean on or hold",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notes:
      "Plain-language mirror of AMPPro Item 1 — prosthetist confirms in person (Stage 3, C1).",
  },
  {
    id: "S2",
    block: "F. Self-Assessment",
    question:
      "While sitting in that same chair, try reaching forward with both hands stretched out in front of you, as far as you can, without lifting your bottom off the seat or grabbing anything for support. How far would you say you can reach?",
    inputType: "single-select",
    options: [
      {
        value: "cannot-reach",
        label:
          "I can't reach forward without losing my balance or needing support",
      },
      {
        value: "reach-unsteady",
        label:
          "I can reach forward a little, but I feel unsteady or need to hold something",
      },
      {
        value: "reach-steady",
        label:
          "I can reach forward about a hand's length or more, easily and steadily",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notes:
      "Plain-language mirror of AMPPro Item 2 — prosthetist confirms in person (Stage 3, C2).",
  },
  {
    id: "S4",
    block: "F. Self-Assessment",
    question:
      "If you have a walker, cane, or someone nearby to help, try standing up from a chair without using your hands or arms to push yourself up. Were you able to do this?",
    inputType: "single-select",
    options: [
      {
        value: "cannot-stand",
        label: "I can't stand up without significant help",
      },
      {
        value: "needs-hands-or-device",
        label:
          "I can stand up, but I need to use my hands/arms or a device to push up",
      },
      {
        value: "stands-without-hands",
        label: "I can stand up without using my hands at all",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes:
      "Only attempt with a stable support nearby. Mirrors AMPPro Item 4. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S5",
    block: "F. Self-Assessment",
    question:
      "Once standing, how steady do you feel in the first few seconds – before you've had a chance to settle?",
    inputType: "single-select",
    options: [
      { value: "unsteady", label: "I feel unsteady and need help right away" },
      {
        value: "wobbly",
        label:
          "I feel a bit wobbly but can manage with a device or support nearby",
      },
      {
        value: "steady",
        label: "I feel steady right away, with no support needed",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 5. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S6",
    block: "F. Self-Assessment",
    question:
      "While standing safely (with support nearby if needed), try standing with your feet apart, about shoulder-width, for 30 seconds without holding on to anything. How did that go?",
    inputType: "single-select",
    options: [
      {
        value: "cannot-stand-wide",
        label: "I couldn't stay standing like that",
      },
      {
        value: "wobbly-or-device",
        label: "I could do it, but I felt wobbly or needed a device",
      },
      { value: "steady-whole-time", label: "I stood the whole time steadily" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 6. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S7",
    block: "F. Self-Assessment",
    question:
      "Now try standing with your feet together for 30 seconds, without holding on to anything. How did that go?",
    inputType: "single-select",
    options: [
      {
        value: "cannot-narrow-base",
        label: "I couldn't keep my feet together without losing balance",
      },
      {
        value: "effort-or-sway",
        label: "I managed it, but with real effort or swaying",
      },
      {
        value: "steady-no-difficulty",
        label: "I stood steadily without any difficulty",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 7. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S8",
    block: "F. Self-Assessment",
    question:
      "While standing with support nearby, try lifting your prosthetic leg/foot off the ground for at least 1 second, balancing only on the leg you still have. Were you able to do that?",
    inputType: "single-select",
    options: [
      { value: "yes", label: "Yes, I could balance for at least 1 second" },
      {
        value: "no",
        label:
          "No, I couldn't lift my prosthetic side or had to grab for support",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes:
      "Not applicable until fitted with a prosthesis — ask only if a device is already in use. K2 vs. K3 discriminator, mirrors AMPPro Item 8. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S9",
    block: "F. Self-Assessment",
    question:
      "Now try the opposite – lift your sound (non-prosthetic) leg off the ground for at least 1 second, balancing on the other side. Were you able to do that?",
    inputType: "single-select",
    options: [
      { value: "yes", label: "Yes, I could balance for at least 1 second" },
      {
        value: "no",
        label: "No, I couldn't lift my leg or had to grab for support",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 9. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S10",
    block: "F. Self-Assessment",
    question:
      "While standing, try reaching forward with both arms as far as you can without taking a step or losing your balance. How far could you reach?",
    inputType: "single-select",
    options: [
      {
        value: "cannot-reach",
        label: "I couldn't reach without stepping or losing balance",
      },
      {
        value: "reach-unsteady",
        label: "I could reach a little, but felt unsteady or needed support",
      },
      {
        value: "reach-steady",
        label: "I could reach about a hand's length or more, steadily",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 10. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S12",
    block: "F. Self-Assessment",
    question:
      "While standing safely, try bending down to pick up a small object (like a sock) from the floor in front of you, without grabbing furniture or losing your balance. How did that go?",
    inputType: "single-select",
    options: [
      {
        value: "cannot-bend",
        label: "I couldn't bend down without losing balance or support",
      },
      {
        value: "difficulty-or-support",
        label: "I managed it with some difficulty or by holding onto something",
      },
      { value: "smooth", label: "I picked it up smoothly without any support" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 12. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S13",
    block: "F. Self-Assessment",
    question:
      "Try lowering yourself back into the chair in a controlled way, without dropping into it or pushing off with your arms. How did that go?",
    inputType: "single-select",
    options: [
      {
        value: "dropped-or-assist",
        label: "I dropped into the chair or needed a lot of help",
      },
      {
        value: "used-arms-or-quick",
        label: "I managed it but used my arms or sat down quickly",
      },
      {
        value: "smooth",
        label: "I lowered myself smoothly without using my arms",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 13. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S14",
    block: "F. Self-Assessment",
    question:
      "When you start walking, do you usually go right away, or do you hesitate, shuffle, or need a moment to get going?",
    inputType: "single-select",
    options: [
      {
        value: "cannot-initiate",
        label: "I have trouble starting or need help to get going",
      },
      {
        value: "hesitates",
        label: "I hesitate or shuffle a bit before I get moving",
      },
      { value: "smooth", label: "I start walking right away, smoothly" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 14. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S15",
    block: "F. Self-Assessment",
    question:
      "When you walk, do your steps feel even on both sides, and do your feet clear the ground without dragging or catching?",
    inputType: "single-select",
    options: [
      {
        value: "uneven-or-drags",
        label: "My steps feel uneven or my foot drags/catches",
      },
      {
        value: "mostly-even",
        label: "Mostly even, with occasional unevenness",
      },
      {
        value: "even-consistent",
        label: "My steps feel even and my feet clear the ground consistently",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 15. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S16",
    block: "F. Self-Assessment",
    question:
      "When you walk across a room, do you walk continuously, or do you stop and start along the way?",
    inputType: "single-select",
    options: [
      {
        value: "stops-repeatedly",
        label: "I stop repeatedly and have trouble continuing",
      },
      {
        value: "one-or-two-stops",
        label: "I mostly keep going, with one or two stops",
      },
      { value: "continuous", label: "I walk continuously without stopping" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 16. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S17",
    block: "F. Self-Assessment",
    question:
      "When you walk in a straight line – say, down a hallway – do you tend to drift to one side?",
    inputType: "single-select",
    options: [
      {
        value: "drifts-noticeably",
        label: "I drift noticeably off a straight line",
      },
      { value: "drifts-a-little", label: "I drift a little" },
      { value: "straight", label: "I walk in a fairly straight line" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 17. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S18",
    block: "F. Self-Assessment",
    question:
      "Can you turn around (180 degrees) while walking and keep going, without having to stop completely first?",
    inputType: "single-select",
    options: [
      {
        value: "cannot-turn-safely",
        label: "I can't turn safely without a lot of help",
      },
      {
        value: "stops-or-device",
        label: "I can turn, but I have to stop or use a device to pivot",
      },
      { value: "smooth", label: "I can turn smoothly without stopping" },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 18. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S19",
    block: "F. Self-Assessment",
    question:
      "If you place a small object on the floor, like a shoe or a thick book (about 4 inches/10cm tall), can you step over it while walking without losing your balance or needing someone close by?",
    inputType: "single-select",
    options: [
      { value: "cannot-step-over", label: "I can't step over it safely" },
      {
        value: "hesitant-or-nearby",
        label:
          "I can step over it, but hesitantly or with someone nearby just in case",
      },
      {
        value: "smooth",
        label: "I can step over it smoothly without any trouble",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Required",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes: "Mirrors AMPPro Item 19. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S20a",
    block: "F. Self-Assessment",
    question: "If you have stairs at home, can you go up 3 steps safely?",
    inputType: "single-select",
    options: [
      {
        value: "cannot-or-help",
        label: "I can't go up, or I need someone's help",
      },
      {
        value: "handrail-effort",
        label: "I can go up using the handrail and it takes real effort",
      },
      {
        value: "easy-alternating",
        label:
          "I can go up easily, alternating feet, barely using the handrail",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Useful",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes:
      "Patients without home stairs may skip; prosthetist will test in person. Mirrors AMPPro Item 20a. Skipped if bilateral + no prosthesis yet.",
  },
  {
    id: "S20b",
    block: "F. Self-Assessment",
    question: "And can you go down 3 steps safely?",
    inputType: "single-select",
    options: [
      {
        value: "cannot-or-help",
        label: "I can't go down, or I need someone's help",
      },
      {
        value: "handrail-effort",
        label: "I can go down using the handrail and it takes real effort",
      },
      {
        value: "easy-alternating",
        label:
          "I can go down easily, alternating feet, barely using the handrail",
      },
    ],
    kLevelSignal: true,
    lcdGate: false,
    validatedBy: "Prosthetist",
    priority: "Useful",
    notApplicableIfBilateralWithoutProsthesis: true,
    notes:
      "Patients without home stairs may skip; prosthetist will test in person. Mirrors AMPPro Item 20b. Skipped if bilateral + no prosthesis yet.",
  },
];

export interface Stage1Answers {
  p0ProsthesisStatus: string;
  p0_1Reason: string;
  p0_1ReasonOther: string;
  p0_2CurrentProsthesisDate: string;
  p0_2CurrentProsthesisDateRight: string;
  p1AmputationLevel: string;
  p1AmputationLevelRight: string;
  p2AmputationSide: string;
  p3AmputationDate: string;
  p3AmputationDateRight: string;
  p4AmputationCause: string;
  p5LimbHealed: string;
  p5LimbHealedRight: string;
  p6ResidualPain: number | null;
  p6ResidualPainRight: number | null;
  p7PhantomPain: string;
  p7PhantomPainSeverity: number | null;
  p8ContraLimb: string;
  p9PreAmpWalking: string;
  p9_1PreAmpWalkingDuration: string;
  p10PreAmpActivity: string;
  p11PreAmpJob: string;
  p11PreAmpJobDetail: string;
  p12PreAmpDevices: string[];
  p13IndoorMobility: string;
  p14OutdoorMobility: string;
  p15TerrainNavigation: string;
  p16SpeedChange: string;
  p17StandingBalance: string;
  p18Endurance: string;
  p19HighImpactDemands: string;
  p19HighImpactDetail: string;
  p20Phq2Q1: number | null;
  p21Phq2Q2: number | null;
  p22Gad2Q1: number | null;
  p23Gad2Q2: number | null;
  p24ProsthesisGoal: string;
  p25RehabWillingness: string;
  p26ReturnToWorkPlan: string;
  s1SittingBalance: string;
  s2SittingReach: string;
  s4RiseFromChair: string;
  s5ImmediateStandBalance: string;
  s6StandBalanceWide: string;
  s7StandBalanceNarrow: string;
  s8SingleLimbProsthetic: string;
  s9SingleLimbSound: string;
  s10StandingReach: string;
  s12PickUpObject: string;
  s13SitDown: string;
  s14GaitInitiation: string;
  s15StepLengthHeight: string;
  s16StepContinuity: string;
  s17PathDeviation: string;
  s18Turning180: string;
  s19StepOverObstacle: string;
  s20aStairsUp: string;
  s20bStairsDown: string;
}

export function createEmptyStage1Answers(): Stage1Answers {
  return {
    p0ProsthesisStatus: "",
    p0_1Reason: "",
    p0_1ReasonOther: "",
    p0_2CurrentProsthesisDate: "",
    p0_2CurrentProsthesisDateRight: "",
    p1AmputationLevel: "",
    p1AmputationLevelRight: "",
    p2AmputationSide: "",
    p3AmputationDate: "",
    p3AmputationDateRight: "",
    p4AmputationCause: "",
    p5LimbHealed: "",
    p5LimbHealedRight: "",
    p6ResidualPain: null,
    p6ResidualPainRight: null,
    p7PhantomPain: "",
    p7PhantomPainSeverity: null,
    p8ContraLimb: "",
    p9PreAmpWalking: "",
    p9_1PreAmpWalkingDuration: "",
    p10PreAmpActivity: "",
    p11PreAmpJob: "",
    p11PreAmpJobDetail: "",
    p12PreAmpDevices: [],
    p13IndoorMobility: "",
    p14OutdoorMobility: "",
    p15TerrainNavigation: "",
    p16SpeedChange: "",
    p17StandingBalance: "",
    p18Endurance: "",
    p19HighImpactDemands: "",
    p19HighImpactDetail: "",
    p20Phq2Q1: null,
    p21Phq2Q2: null,
    p22Gad2Q1: null,
    p23Gad2Q2: null,
    p24ProsthesisGoal: "",
    p25RehabWillingness: "",
    p26ReturnToWorkPlan: "",
    s1SittingBalance: "",
    s2SittingReach: "",
    s4RiseFromChair: "",
    s5ImmediateStandBalance: "",
    s6StandBalanceWide: "",
    s7StandBalanceNarrow: "",
    s8SingleLimbProsthetic: "",
    s9SingleLimbSound: "",
    s10StandingReach: "",
    s12PickUpObject: "",
    s13SitDown: "",
    s14GaitInitiation: "",
    s15StepLengthHeight: "",
    s16StepContinuity: "",
    s17PathDeviation: "",
    s18Turning180: "",
    s19StepOverObstacle: "",
    s20aStairsUp: "",
    s20bStairsDown: "",
  };
}

export interface Stage1FieldBinding {
  field: keyof Stage1Answers;
  detailField?: keyof Stage1Answers;
  severityField?: keyof Stage1Answers;
  numeric?: boolean;
}

export const STAGE1_FIELD_BINDINGS: Record<string, Stage1FieldBinding> = {
  P0: { field: "p0ProsthesisStatus" },
  "P0.1": { field: "p0_1Reason", detailField: "p0_1ReasonOther" },
  "P0.2": { field: "p0_2CurrentProsthesisDate" },
  "P0.2-right": { field: "p0_2CurrentProsthesisDateRight" },
  P1: { field: "p1AmputationLevel" },
  "P1-right": { field: "p1AmputationLevelRight" },
  P2: { field: "p2AmputationSide" },
  P3: { field: "p3AmputationDate" },
  "P3-right": { field: "p3AmputationDateRight" },
  P4: { field: "p4AmputationCause" },
  P5: { field: "p5LimbHealed" },
  "P5-right": { field: "p5LimbHealedRight" },
  P6: { field: "p6ResidualPain" },
  "P6-right": { field: "p6ResidualPainRight" },
  P7: { field: "p7PhantomPain", severityField: "p7PhantomPainSeverity" },
  P8: { field: "p8ContraLimb" },
  P9: { field: "p9PreAmpWalking" },
  "P9.1": { field: "p9_1PreAmpWalkingDuration" },
  P10: { field: "p10PreAmpActivity" },
  P11: { field: "p11PreAmpJob", detailField: "p11PreAmpJobDetail" },
  P12: { field: "p12PreAmpDevices" },
  P13: { field: "p13IndoorMobility" },
  P14: { field: "p14OutdoorMobility" },
  P15: { field: "p15TerrainNavigation" },
  P16: { field: "p16SpeedChange" },
  P17: { field: "p17StandingBalance" },
  P18: { field: "p18Endurance" },
  P19: { field: "p19HighImpactDemands", detailField: "p19HighImpactDetail" },
  P20: { field: "p20Phq2Q1", numeric: true },
  P21: { field: "p21Phq2Q2", numeric: true },
  P22: { field: "p22Gad2Q1", numeric: true },
  P23: { field: "p23Gad2Q2", numeric: true },
  P24: { field: "p24ProsthesisGoal" },
  P25: { field: "p25RehabWillingness" },
  P26: { field: "p26ReturnToWorkPlan" },
  S1: { field: "s1SittingBalance" },
  S2: { field: "s2SittingReach" },
  S4: { field: "s4RiseFromChair" },
  S5: { field: "s5ImmediateStandBalance" },
  S6: { field: "s6StandBalanceWide" },
  S7: { field: "s7StandBalanceNarrow" },
  S8: { field: "s8SingleLimbProsthetic" },
  S9: { field: "s9SingleLimbSound" },
  S10: { field: "s10StandingReach" },
  S12: { field: "s12PickUpObject" },
  S13: { field: "s13SitDown" },
  S14: { field: "s14GaitInitiation" },
  S15: { field: "s15StepLengthHeight" },
  S16: { field: "s16StepContinuity" },
  S17: { field: "s17PathDeviation" },
  S18: { field: "s18Turning180" },
  S19: { field: "s19StepOverObstacle" },
  S20a: { field: "s20aStairsUp" },
  S20b: { field: "s20bStairsDown" },
};

const STAGE1_ANSWERS_STORAGE_KEY = "intake_stage1_answers";

export function loadStage1Answers(): Stage1Answers {
  try {
    const raw = sessionStorage.getItem(STAGE1_ANSWERS_STORAGE_KEY);
    if (!raw) return createEmptyStage1Answers();
    return { ...createEmptyStage1Answers(), ...JSON.parse(raw) };
  } catch {
    return createEmptyStage1Answers();
  }
}

export function saveStage1Answers(answers: Stage1Answers): void {
  sessionStorage.setItem(STAGE1_ANSWERS_STORAGE_KEY, JSON.stringify(answers));
}

export function clearStage1Answers(): void {
  sessionStorage.removeItem(STAGE1_ANSWERS_STORAGE_KEY);
}

function isQuestionApplicable(
  question: Stage1Question,
  answers: Stage1Answers,
): boolean {
  const isBilateral = answers.p2AmputationSide === "bilateral";
  if (question.notApplicableIfBilateral && isBilateral) return false;
  if (question.onlyIfBilateral && !isBilateral) return false;
  if (
    question.notApplicableIfBilateralWithoutProsthesis &&
    isBilateral &&
    answers.p0ProsthesisStatus !== "wears-regularly"
  ) {
    return false;
  }
  if (question.notApplicableIfNoProsthesis) {
    const hasProsthesis =
      answers.p0ProsthesisStatus === "wears-regularly" ||
      answers.p0ProsthesisStatus === "has-not-wearing";
    if (!hasProsthesis) return false;
  }
  return true;
}

// Stage 1 is presented one block (PDF "Step") at a time — every question that
// belongs to a block is shown together on the same intake step.
export const STAGE1_BLOCK_ORDER: Stage1Block[] = [
  "A. About your amputation",
  "B. Your life before",
  "C. How you move today",
  "D. How you're feeling",
  "E. Your goals",
  "F. Self-Assessment",
];

export function getBlockId(block: Stage1Block): string {
  return block.charAt(0);
}

export function getBlockFromId(blockId: string): Stage1Block | undefined {
  return STAGE1_BLOCK_ORDER.find((b) => getBlockId(b) === blockId);
}

export function getQuestionsForBlock(
  blockId: string,
  answers: Stage1Answers,
): Stage1Question[] {
  const block = getBlockFromId(blockId);
  if (!block) return [];
  return STAGE1_QUESTIONS.filter(
    (q) => q.block === block && isQuestionApplicable(q, answers),
  );
}

export function getBlockPosition(blockId: string): number {
  const block = getBlockFromId(blockId);
  return block ? STAGE1_BLOCK_ORDER.indexOf(block) : -1;
}

export function getNextBlockId(blockId: string): string | null {
  const next = STAGE1_BLOCK_ORDER[getBlockPosition(blockId) + 1];
  return next ? getBlockId(next) : null;
}

export function getPreviousBlockId(blockId: string): string | null {
  const position = getBlockPosition(blockId);
  const prev = position > 0 ? STAGE1_BLOCK_ORDER[position - 1] : undefined;
  return prev ? getBlockId(prev) : null;
}

export function getTotalIntakeSteps(): number {
  return 1 + STAGE1_BLOCK_ORDER.length + 4;
}

export function isQuestionAnswered(
  question: Stage1Question,
  binding: Stage1FieldBinding,
  answers: Stage1Answers,
): boolean {
  if (question.priority === "Useful") return true;
  if (question.inputType === "multi-select") {
    return ((answers[binding.field] as string[]) ?? []).length > 0;
  }
  if (question.inputType === "slider") return true;
  return !!answers[binding.field];
}

export function isBlockComplete(
  blockId: string,
  answers: Stage1Answers,
): boolean {
  return getQuestionsForBlock(blockId, answers).every((q) => {
    const binding = STAGE1_FIELD_BINDINGS[q.id];
    return !binding || isQuestionAnswered(q, binding, answers);
  });
}
